/**
 * Notification Feature Types
 *
 * Centralized type definitions for push subscriptions, preferences,
 * the GDPR-minimal push payload contract, and notification history.
 */

// ─── Notification History (DB-backed, Phase 6e) ──────────────────────────────

/** Single deduped notification row from notification_deliveries */
export interface NotificationHistoryItem {
  id: string;
  event_type: string;
  event_key: string;
  event_date: string; // YYYY-MM-DD
  status: string;
  sent_at: string; // ISO timestamptz
  read_at: string | null; // null = unread
  is_update: boolean;
  queue_id: string | null; // links to notification_send_queue.id
  payload?: Record<string, unknown>; // populated natively in fn_notifications_get_my_history
}

/**
 * Shared payload contract — both sources must return this exact shape:
 *   1) fn_teacher_dashboard_initial_load → notification_bootstrap
 *   2) fn_notifications_get_my_history RPC
 */
export interface NotificationHistoryPayload {
  unread_count: number;
  items: NotificationHistoryItem[];
}

// ─── Delivery Snapshot (Admin detail, Phase 6e.1) ────────────────────────────

export interface DeliverySnapshotChannel {
  channel: "push" | "email";
  status: string;
  sent_at: string | null;
  read_at: string | null;
  is_update: boolean;
}

export interface DeliverySnapshotResponse {
  entries: Array<Record<string, unknown>>;
  diff_entries?: DiffEntry[];
  push_preview: { title: string; body: string } | null;
  email_preview: { subject: string } | null;
  channels: DeliverySnapshotChannel[];
  is_update: boolean;
}

// ─── Diff Types (Phase 6f+6g) ────────────────────────────────────────────────

/** Per-entry diff status between consecutive sends */
export type DiffStatus = "added" | "removed" | "changed" | "unchanged";

/** Single diff entry — carries the current entry, optional old entry, and status */
export interface DiffEntry {
  entry: Record<string, unknown>; // current entry (or old for removals)
  old_entry?: Record<string, unknown>; // previous entry (for 'changed' only)
  diff_status: DiffStatus;
}

/** Response from fn_notifications_get_my_delivery_snapshot (teacher-safe) */
export interface TeacherDeliverySnapshotResponse {
  entries: Array<Record<string, unknown>>;
  diff_entries?: DiffEntry[];
  push_preview: { title: string; body: string } | null;
  email_preview: { subject: string } | null;
  channels: DeliverySnapshotChannel[];
  is_update: boolean;
}

// ─── Preferences ──────────────────────────────────────────────────────────────

export interface NotificationPreferences {
  user_profile_id: string;
  school_id: string;
  push_enabled: boolean;
  email_enabled: boolean;
  allow_substitution: boolean;
  allow_calendar: boolean;
  /** Umfrage / Bulletin / Schulinfo-style announcements. */
  allow_school_info: boolean;
  /** Parent-side: child-specific updates (stay_home, future sick-report confirmations, etc.). */
  allow_child_updates: boolean;
  quiet_days: number[]; // JS getDay(): 0=Sun … 6=Sat
  quiet_start: string | null; // "HH:MM"
  quiet_end: string | null;
}

/** Client-side defaults when no DB row exists yet */
export const DEFAULT_PREFERENCES: Omit<
  NotificationPreferences,
  "user_profile_id" | "school_id"
> = {
  push_enabled: true,
  email_enabled: false,
  allow_substitution: true,
  allow_calendar: true,
  allow_school_info: true,
  allow_child_updates: true,
  quiet_days: [],
  quiet_start: null,
  quiet_end: null,
};

// ─── Push Subscriptions ───────────────────────────────────────────────────────

/** UI-safe type: no crypto keys (p256dh / auth_key stay in API layer only) */
export interface PushSubscriptionRecord {
  id: string;
  user_profile_id: string;
  school_id: string;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
}

// ─── Permission ───────────────────────────────────────────────────────────────

export type PermissionState = "default" | "granted" | "denied" | "unsupported";

// ─── Push Payload Contract ────────────────────────────────────────────────────
// GDPR-minimal, stable across Web Push and future native adapters.

export interface PushPayload {
  type: "notification";
  event_type: string; // e.g. "substitution.published", "message.new"
  event_key: string; // collapse key for dedup
  url: string; // relative deep link
  title: string; // generic, NO PII
  body: string; // generic, NO PII
  meta?: Record<string, unknown>;
}

// ─── Substitution Entry (teacher detail view) ─────────────────────────────────

/** Single substitution entry as returned by the preview Edge Function */
export interface SubstitutionEntry {
  period: string;
  subject: string;
  class: string;
  room?: string | null;
  comment?: string | null;
  type: "lesson" | "aufsicht";
  aufsicht_ort?: string | null;
}

// ─── SW Message Types ─────────────────────────────────────────────────────────

export interface SWNotificationReceived {
  type: "NOTIFICATION_RECEIVED";
  payload: PushPayload;
}

export interface SWNotificationClicked {
  type: "NOTIFICATION_CLICKED";
  url: string;
}

export type SWMessage = SWNotificationReceived | SWNotificationClicked;

// ─── Preview Types (Admin) ───────────────────────────────────────────────────

export interface NotificationPreviewSummary {
  total_teachers: number;
  with_changes: number;
  without_changes: number;
}

export interface NotificationDiffItem {
  label: string;
  before: string | null;
  after: string | null;
  type: "added" | "changed" | "removed";
}

export interface NotificationPreviewTeacher {
  teacher_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  push_enabled: boolean;
  push_on_update?: boolean;
  /** Whether the teacher actually has a push_subscriptions row — gates
   *  whether `push_enabled` translates into real deliverability. Returned
   *  by fn_notifications_get_substitution_targets. May be undefined if
   *  the RPC predates the field. */
  has_push_subscription?: boolean;
  email_enabled: boolean;
  email_on_update?: boolean;
  /**
   * Whether this teacher has at least one registered push_subscriptions
   * row. push_enabled=true alone doesn't mean we can reach them — the
   * teacher must also have granted browser permission + completed the
   * Web Push subscription handshake. The RPC computes this server-side
   * via EXISTS(push_subscriptions).
   */
  has_push_subscription?: boolean;
  event_key: string;
  entries: Array<Record<string, unknown>>;
  count: number;
  is_update: boolean;
  has_changes: boolean;
  will_push?: boolean;
  will_email?: boolean;
  push_preview?: PushPayload;
  email_preview_html?: string;
  diff?: NotificationDiffItem[];
}

export interface NotificationPreviewResponse {
  date: string;
  is_update: boolean;
  teachers: NotificationPreviewTeacher[];
  summary: NotificationPreviewSummary;
}
